import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import { AdminRegionalOperations, type RegionalOperationsView } from "./AdminRegionalOperations";
import { AdminRegionalGovernance } from "./AdminRegionalGovernance";

type Tab="GOVERNANCE"|"SCHOOLS"|"SUBMISSIONS"|"DISTRIBUTION"|"READINESS"|"TEMPLATES";
type SchoolRow={
  schoolId:string;schoolName:string;schoolCode?:string|null;academicYearId?:string|null;yearCode?:string|null;
  studentCount:number;classCount:number;staffCount:number;assessmentAssigned:number;assessmentCompleted:number;
  assessmentCompletionRate:number;activityCompleted:number;counselingCompleted:number;badgeIssued:number;badgeClaimed:number;
  badgeClaimRate:number;studentsWithoutClass:number;classesWithoutStaff:number;badgeDeliveryIssues:number;issueCount:number;
};
type Tower={
  root:{organizationId:string;name:string;code?:string|null;type:string};
  summary:{schoolCount:number;studentCount:number;classCount:number;staffCount:number;assessmentAssigned:number;assessmentCompleted:number;activityCompleted:number;counselingCompleted:number;badgeIssued:number;badgeClaimed:number;dataQualityIssues:number};
  schools:SchoolRow[];
};
type Version={
  versionId:string;versionNumber:number;versionLabel:string;status:string;title:string;subtitle?:string|null;
  footer?:string|null;sections:string[];branding:Record<string,unknown>;createdAt:string;publishedAt?:string|null;
};
type Template={templateId:string;templateKey:string;name:string;audience:string;status:string;versions:Version[]};

const DEFAULT_SECTIONS=["SUMMARY","GRADES","PROGRAMS","BADGES","QUALITY"];
const SECTION_LABEL:Record<string,string>={SUMMARY:"운영 개요",GRADES:"학년 현황",PROGRAMS:"프로그램",BADGES:"디지털배지",QUALITY:"데이터 점검"};

function navigate(path:string){
  window.history.pushState({},"",path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
function csvCell(value:unknown){const text=value==null?"":String(value);return '"'+text.replace(/"/g,'""')+'"'}
function downloadCsv(rows:Array<Record<string,unknown>>,name:string){
  if(!rows.length)return;
  const headers=Array.from(new Set(rows.flatMap((row)=>Object.keys(row))));
  const body=[headers.map(csvCell).join(","),...rows.map((row)=>headers.map((key)=>csvCell(row[key])).join(","))].join("\r\n");
  const blob=new Blob(["\uFEFF",body],{type:"text/csv;charset=utf-8"});
  const url=URL.createObjectURL(blob);const a=document.createElement("a");a.href=url;a.download=name;a.click();URL.revokeObjectURL(url);
}

export function AdminControlTowerPage({client,rootOrganizationId,canManagePlatformAccess}:{client:PlatformClient;rootOrganizationId:string;canManagePlatformAccess:boolean}){
  const requestedTab=new URLSearchParams(window.location.search).get("tab");
  const initialTab:Tab=
    requestedTab==="schools"?"SCHOOLS":
    requestedTab==="submissions"?"SUBMISSIONS":
    requestedTab==="distribution"?"DISTRIBUTION":
    requestedTab==="readiness"?"READINESS":
    requestedTab==="templates"?"TEMPLATES":
    "GOVERNANCE";
  const [tab,setTab]=useState<Tab>(initialTab);
  const [tower,setTower]=useState<Tower|null>(null);
  const [templates,setTemplates]=useState<Template[]>([]);
  const [selectedTemplateId,setSelectedTemplateId]=useState("");
  const [refresh,setRefresh]=useState(0);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");

  const [newTemplate,setNewTemplate]=useState(false);
  const [templateKey,setTemplateKey]=useState("SCHOOL_CAREER_ANNUAL");
  const [templateName,setTemplateName]=useState("학교 진로교육 연간 운영보고");
  const [versionLabel,setVersionLabel]=useState("");
  const [title,setTitle]=useState("");
  const [subtitle,setSubtitle]=useState("");
  const [footer,setFooter]=useState("");
  const [sections,setSections]=useState<string[]>(DEFAULT_SECTIONS);

  useEffect(()=>{
    let active=true;
    setError("");
    Promise.all([
      client.rpc("admin_control_tower",{p_root_organization_id:rootOrganizationId}),
      client.rpc("admin_report_template_workspace",{p_owner_organization_id:rootOrganizationId})
    ]).then(([towerResult,templateResult])=>{
      if(!active)return;
      const first=towerResult.error || templateResult.error;
      if(first){setError(first.message);return;}
      setTower(towerResult.data as Tower);
      const next=(templateResult.data || []) as Template[];
      setTemplates(next);
      setSelectedTemplateId((current)=>next.some((item)=>item.templateId===current)?current:next[0]?.templateId || "");
    });
    return()=>{active=false;};
  },[client,rootOrganizationId,refresh]);

  const selected=useMemo(()=>templates.find((item)=>item.templateId===selectedTemplateId) || null,[templates,selectedTemplateId]);
  const latest=selected?.versions[0] || null;

  useEffect(()=>{
    if(newTemplate){
      setVersionLabel("v1.0");setTitle("{{yearCode}}학년도 진로교육 운영·성과 보고서");setSubtitle("{{schoolName}} · RS CAREER ERP");setFooter("본 보고서는 RS CAREER ERP의 해당 학년도 운영 데이터를 기준으로 생성되었습니다.");setSections(DEFAULT_SECTIONS);
      return;
    }
    if(latest){
      setVersionLabel("");
      setTitle(latest.title);
      setSubtitle(latest.subtitle || "");
      setFooter(latest.footer || "");
      setSections(latest.sections?.length ? latest.sections : DEFAULT_SECTIONS);
    }
  },[selectedTemplateId,newTemplate,latest?.versionId]);

  async function run(key:string,fn:()=>PromiseLike<{data?:unknown;error?:{message:string}|null}>,success:string){
    setBusy(key);setError("");setMessage("");
    try{
      const result=await fn();if(result.error)throw new Error(result.error.message);
      setMessage(success);setRefresh((value)=>value+1);return result.data;
    }catch(err){setError(err instanceof Error?err.message:"처리 중 오류가 발생했습니다.");return null}
    finally{setBusy("")}
  }

  async function createVersion(){
    const result=await run("create-version",()=>client.rpc("admin_create_report_template_version",{
      p_owner_organization_id:rootOrganizationId,
      p_template_id:newTemplate?null:selectedTemplateId || null,
      p_template_key:templateKey.trim(),
      p_name:templateName.trim(),
      p_version_label:versionLabel.trim(),
      p_title:title.trim(),
      p_subtitle:subtitle.trim() || null,
      p_footer:footer.trim() || null,
      p_sections:sections
    }),"보고서 템플릿 새 버전을 초안으로 저장했습니다.");
    if(result){
      const value=result as {templateId:string};
      setNewTemplate(false);setSelectedTemplateId(value.templateId);setVersionLabel("");
    }
  }

  async function publish(versionId:string){
    await run("publish",()=>client.rpc("admin_publish_report_template_version",{p_owner_organization_id:rootOrganizationId,p_version_id:versionId}),"선택한 보고서 템플릿 버전을 배포했습니다.");
  }

  function exportSchools(){
    if(!tower)return;
    downloadCsv(tower.schools.map((row)=>({
      학교:row.schoolName,학교코드:row.schoolCode || "",학년도:row.yearCode || "",
      학생:row.studentCount,학급:row.classCount,교직원:row.staffCount,
      검사배정:row.assessmentAssigned,검사완료:row.assessmentCompleted,검사완료율:row.assessmentCompletionRate+"%",
      활동완료:row.activityCompleted,상담완료:row.counselingCompleted,
      배지발급:row.badgeIssued,배지수령:row.badgeClaimed,배지수령률:row.badgeClaimRate+"%",
      학급미배정학생:row.studentsWithoutClass,담당교사미배정학급:row.classesWithoutStaff,배지전달문제:row.badgeDeliveryIssues
    })),`RS_CAREER_${tower.root.name}_학교비교.csv`);
  }

  if(!tower)return <p className="muted">Control Tower를 불러오는 중…</p>;

  return (
    <section className="control-tower-page">
      <header className="control-tower-head">
        <div><p className="eyebrow">Regional / School Operations Governance</p><h2>Regional Control Tower</h2><p>{tower.root.name} 하위 학교의 운영·성과·지원 신호·공식 보고 흐름을 학생 원문 없이 집계합니다.</p></div>
        <div className="tower-school-count"><span>학교</span><strong>{tower.summary.schoolCount}</strong></div>
      </header>
      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      <div className="tower-kpis">
        <Kpi label="학생" value={tower.summary.studentCount}/><Kpi label="학급" value={tower.summary.classCount}/><Kpi label="교직원" value={tower.summary.staffCount}/>
        <Kpi label="검사 완료" value={tower.summary.assessmentCompleted}/><Kpi label="활동 완료" value={tower.summary.activityCompleted}/><Kpi label="상담 완료" value={tower.summary.counselingCompleted}/>
        <Kpi label="배지 발급" value={tower.summary.badgeIssued}/><Kpi label="배지 수령" value={tower.summary.badgeClaimed}/><Kpi label="데이터 점검" value={tower.summary.dataQualityIssues}/>
      </div>

      <nav className="tower-tabs">
        <button className={tab==="GOVERNANCE"?"active":""} onClick={()=>setTab("GOVERNANCE")}>지역 운영관제</button>
        <button className={tab==="SCHOOLS"?"active":""} onClick={()=>setTab("SCHOOLS")}>학교 비교</button>
        <button className={tab==="SUBMISSIONS"?"active":""} onClick={()=>setTab("SUBMISSIONS")}>보고서 제출함</button>
        <button className={tab==="DISTRIBUTION"?"active":""} onClick={()=>setTab("DISTRIBUTION")}>다학교 검사배포</button>
        <button className={tab==="READINESS"?"active":""} onClick={()=>setTab("READINESS")}>운영 준비상태</button>
        <button className={tab==="TEMPLATES"?"active":""} onClick={()=>setTab("TEMPLATES")}>보고서 템플릿</button>
      </nav>

      {tab==="GOVERNANCE" && (
        <AdminRegionalGovernance
          client={client}
          rootOrganizationId={rootOrganizationId}
          canManagePlatformAccess={canManagePlatformAccess}
          onOpenSection={(section)=>setTab(section)}
        />
      )}

      {tab==="SCHOOLS" && (
        <section className="tower-card">
          <div className="panel-title"><strong>학교별 운영 비교</strong><button className="text-button" onClick={exportSchools}>CSV 내보내기</button></div>
          <div className="tower-school-table">
            <div className="tower-school-row head"><span>학교</span><span>학생</span><span>교직원</span><span>검사</span><span>활동</span><span>상담</span><span>배지</span><span>수령률</span><span>점검</span><span>관리</span></div>
            {tower.schools.map((school)=>(
              <div className="tower-school-row" key={school.schoolId}>
                <span><strong>{school.schoolName}</strong><small>{school.yearCode || "활성 학년도 없음"} · {school.schoolCode || "코드 없음"}</small></span>
                <span>{school.studentCount}</span><span>{school.staffCount}</span><span>{school.assessmentCompleted}/{school.assessmentAssigned}</span><span>{school.activityCompleted}</span><span>{school.counselingCompleted}</span><span>{school.badgeClaimed}/{school.badgeIssued}</span><span>{school.badgeClaimRate}%</span>
                <span className={school.issueCount>0?"warn":""}>{school.issueCount}</span>
                {canManagePlatformAccess && <div className="tower-row-actions"><button onClick={()=>navigate(`/access?organization=${encodeURIComponent(school.schoolId)}`)}>사용자·권한</button><button onClick={()=>navigate("/organizations")}>학교정보</button></div>}
              </div>
            ))}
            {!tower.schools.length && <p className="case-empty">하위 활성 학교가 없습니다.</p>}
          </div>
        </section>
      )}

      {(tab==="SUBMISSIONS" || tab==="DISTRIBUTION" || tab==="READINESS") && (
        <AdminRegionalOperations client={client} rootOrganizationId={rootOrganizationId} view={tab as RegionalOperationsView} />
      )}

      {tab==="TEMPLATES" && (
        <div className="template-layout">
          <aside className="tower-card">
            <div className="panel-title"><strong>보고서 템플릿</strong><button className="text-button" onClick={()=>setNewTemplate(true)}>새 템플릿</button></div>
            <div className="template-list">
              {templates.map((item)=><button key={item.templateId} className={!newTemplate&&selectedTemplateId===item.templateId?"active":""} onClick={()=>{setNewTemplate(false);setSelectedTemplateId(item.templateId)}}><span><strong>{item.name}</strong><small>{item.templateKey}</small></span><b>{item.versions.find((version)=>version.status==="PUBLISHED")?.versionLabel || "미배포"}</b></button>)}
            </div>
          </aside>

          <section className="tower-card template-editor">
            <div className="panel-title"><div><strong>{newTemplate?"새 보고서 템플릿":"새 버전 초안"}</strong><span>배포 전에는 학교 리포트에 노출되지 않습니다.</span></div></div>
            {newTemplate && <div className="template-two"><label>Template Key<input value={templateKey} onChange={(e)=>setTemplateKey(e.target.value)} /></label><label>템플릿명<input value={templateName} onChange={(e)=>setTemplateName(e.target.value)} /></label></div>}
            <div className="template-two"><label>버전 라벨<input value={versionLabel} onChange={(e)=>setVersionLabel(e.target.value)} placeholder="예: v2.0" /></label><label>보고서 제목<input value={title} onChange={(e)=>setTitle(e.target.value)} /></label></div>
            <label>부제<input value={subtitle} onChange={(e)=>setSubtitle(e.target.value)} /></label>
            <label>하단 문구<textarea rows={3} value={footer} onChange={(e)=>setFooter(e.target.value)} /></label>
            <div className="template-sections"><strong>보고서 섹션</strong>{DEFAULT_SECTIONS.map((key)=><label key={key}><input type="checkbox" checked={sections.includes(key)} onChange={()=>setSections((current)=>current.includes(key)?current.filter((item)=>item!==key):[...current,key])}/>{SECTION_LABEL[key]}</label>)}</div>
            <button className="primary" disabled={busy!=="" || !versionLabel.trim() || !title.trim() || !sections.length} onClick={createVersion}>새 버전 초안 저장</button>

            {!newTemplate && selected && (
              <div className="template-version-list">
                <div className="panel-title"><strong>버전 이력</strong><span>{selected.versions.length}개</span></div>
                {selected.versions.map((version)=><article key={version.versionId}><span><strong>{version.versionLabel} · {version.status}</strong><small>{version.title}</small></span>{version.status==="DRAFT" ? <button className="primary" disabled={busy!==""} onClick={()=>publish(version.versionId)}>배포</button> : <b>{version.status==="PUBLISHED"?"현재 배포":"이전 버전"}</b>}</article>)}
              </div>
            )}
          </section>
        </div>
      )}
    </section>
  );
}

function Kpi({label,value}:{label:string;value:number}){return <div className="tower-kpi"><span>{label}</span><strong>{value}</strong></div>}
